import {getFiltersFromConnectionIds} from 'instruments/connection-files/ConnectionFilesTable';
import {InstrumentServiceDetails} from 'instruments/connections/ConnectionConstants';
import {
  isBrowseFilesAdapter,
  isTableExportAdapter,
} from 'instruments/instrument-data/InstrumentDataConstants';
import {pollUntil} from 'instruments/instrument-data/InstrumentDataDAO';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {FilterKey, Operator, SearchType, SortKey} from 'search/SearchConstants';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';

export enum RetrievalMode {
  SYNCED = 'synced',
  ON_DEMAND = 'on_demand',
}

export enum OnDemandRetrievalKind {
  // The connection lists its files (BROWSE_FILES) and retrieves the ones the user picks (COPY_OBJECT).
  BROWSE_FILES = 'browse_files',
  // The connection retrieves data for the run's fields with a single COPY_OBJECT request.
  DIRECT_REQUEST = 'direct_request',
}

/**
 * A retrieval the user has set up but not yet run. `retrieve` copies the files to Benchling and resolves to
 * their data catalog file ids.
 */
export interface PendingRetrieval {
  retrieve: () => Promise<Array<string>>;
}

export interface CopyObjectResponseParams {
  path?: string;
}

/**
 * How a connection retrieves data on demand, or null if it can't.
 */
export function getOnDemandRetrievalKind(
  instrumentServiceDetails: InstrumentServiceDetails
): OnDemandRetrievalKind | null {
  if (isBrowseFilesAdapter(instrumentServiceDetails.adapterId)) {
    return OnDemandRetrievalKind.BROWSE_FILES;
  }
  if (
    instrumentServiceDetails.isFileBrowsing === false &&
    !isTableExportAdapter(instrumentServiceDetails.adapterId)
  ) {
    return OnDemandRetrievalKind.DIRECT_REQUEST;
  }
  return null;
}

/**
 * COPY_OBJECT params that tell the instruments service to use the credentials the user logged in with. The
 * service only needs the username and substitutes the stored password for the placeholder.
 */
export function getLoginParams(
  loginUsername: string | null
): {username: string; password: string} | undefined {
  return loginUsername ? {username: loginUsername, password: '******'} : undefined;
}

export function getRetrievalErrorMessage(error: unknown): string {
  // jQuery XHR rejections include responseJSON with server error details
  const serverMessage = (error as {responseJSON?: {userMessage?: string}})?.responseJSON?.userMessage;
  if (serverMessage) {
    return serverMessage;
  }
  if (error instanceof Error) {
    return error.message.includes('Polling failed')
      ? 'The connection took too long to respond. Please try again later.'
      : error.message;
  }
  return 'Please contact Benchling support if the issue persists.';
}

/**
 * Waits for a file that COPY_OBJECT copied to Benchling to appear in the data catalog.
 */
export function searchForRetrievedFile(filePath: string, connectionId: string): Promise<FileListingGraphQL> {
  // The COPY_OBJECT response may return a full path (e.g. chrom://host/path/file.seq).
  // The data catalog indexes files by basename only.
  const basename = filePath.split('/').pop() ?? filePath;
  return pollUntil(
    async () => {
      const filters = getFiltersFromConnectionIds([connectionId]);
      filters.push({
        key: FilterKey.NAMES,
        operator: Operator.IS_ONE_OF,
        value: [basename],
      });
      return (await GlobalSearchActions.doGlobalSearch(
        {
          offset: 0,
          limit: 1,
          sorts: [{key: SortKey.SORT_CREATED_AT_KEY, reverse: true}],
          filters,
          query: '',
        },
        SearchType.GLOBAL,
        'lab_automation:PIPELINE_FILE_POLL'
      )) as GlobalSearchResponse;
    },
    (response: GlobalSearchResponse) => response.ids.length === 1,
    3 * 1000, // 3 seconds between retries
    30 * 1000 // 30 seconds max wait
  ).then(
    (result) => result.filesById[result.ids[0]],
    () => {
      throw new Error('Could not find the retrieved file in the data catalog.');
    }
  );
}
